import { useState, useCallback } from 'react';
import { apiPut, ApiResponse, ApiError } from '../../services/api.service';

interface Group {
  id: number;
  name: string;
  description?: string;
  group_code: number;
  creator_id: number;
  created_at: string;
  updated_at: string;
  members?: any[];
}

interface JoinGroupData {
  group_code: number;
}

interface UseJoinGroupReturn {
  joinGroup: (groupCode: number) => Promise<Group | null>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Custom hook to join a group using group code
 * @returns Function to join group, loading state, error state, and success state
 */
export function useJoinGroup(): UseJoinGroupReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const joinGroup = useCallback(
    async (groupCode: number): Promise<Group | null> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const data: JoinGroupData = { group_code: groupCode };

        const response: ApiResponse<Group> = await apiPut<Group>(
          '/groups/join',
          data,
          {
            notification: {
              enabled: true,
              successMessage: 'Erfolgreich der Gruppe beigetreten!',
              errorMessage: 'Fehler beim Beitreten zur Gruppe. Bitte überprüfen Sie den Code und versuchen Sie es erneut.',
            },
          }
        );

        if (response.success && response.data) {
          setSuccess(true);
          return response.data;
        } else {
          setError(response.error || 'Fehler beim Beitreten zur Gruppe');
          return null;
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Ein unerwarteter Fehler ist aufgetreten');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    joinGroup,
    loading,
    error,
    success,
  };
}